import { useStoreSelector } from "./useStoreSelector";
import { EventSystemNames } from "../constants";
import { AppStore } from '../stores/AppStore';
import uuid from 'react-uuid';

// Constants for timeout values
const BRIEF_FLAG_TIMEOUT_MS = 5;

export const useIsBusy = () => {
    return useStoreSelector(AppStore, (state) => state.isBusy);
}
export const useSetIsBusy = () => {
    return useStoreSelector(AppStore, (state) => state.setIsBusy);
}

export const useDarkmode = () => {
    return useStoreSelector(AppStore, (state) => state.darkmode);
}
export const useSetDarkmode = () => {
    return useStoreSelector(AppStore, (state) => state.setDarkmode);
}

export const useApplicationRights = () => {
    return useStoreSelector(AppStore, (state) => state.applicationRights);
}
export const useSetApplicationRights = () => {
    return useStoreSelector(AppStore, (state) => state.setApplicationRights);
}

export const useConfiguration = () => {
    return useStoreSelector(AppStore, (state) => state.configuration);
}
export const useSetConfiguration = () => {
    return useStoreSelector(AppStore, (state) => state.setConfiguration);
}

export const useNotificationMessage = () => {
    return useStoreSelector(AppStore, (state) => state.notificationMessage);
}
export const useSetNotificationMessage = () => {
    return useStoreSelector(AppStore, (state) => state.setNotificationMessage);
}

export const useHeaderContent = () => {
    return useStoreSelector(AppStore, (state) => state.headerContent);
}
export const useSetHeaderContent = () => {
    const setHeaderContent = useStoreSelector(AppStore, (state) => state.setHeaderContent);

    return (title: string, subTitle = "", canGoBack = true, canCopyTitle = false, canCopySubTitle = false) => {
        setHeaderContent({
            title: title,
            subTitle: subTitle,
            canGoBack: canGoBack,
            canCopyTitle: canCopyTitle,
            canCopySubTitle: canCopySubTitle
        });
    }
}

export const useCloseNotificationMessageId = () => {
    return useStoreSelector(AppStore, (state) => state.closeNotificationMessageId);
}
export const useSetCloseNotificationMessageId = () => {
    return useStoreSelector(AppStore, (state) => state.setCloseNotificationMessageId);
}

export const useReloadAlerts = () => {
    return useStoreSelector(AppStore, (state) => state.reloadAlerts);
}
export const useSetReloadAlerts = () => {
    const setReloadAlerts = useStoreSelector(AppStore, (state) => state.setReloadAlerts);
    return () => {
        setReloadAlerts(true);
        setTimeout(() => {
            setReloadAlerts(false);
        }, BRIEF_FLAG_TIMEOUT_MS);
    }
}

export const useMapEvent = () => {
    return useStoreSelector(AppStore, (state) => state.mapEvent);
}

export const useSetMapEvent = () => {
    return useStoreSelector(AppStore, (state) => state.setMapEvent);
}

export const useFeatureMouseOverEvent = () => {
    const setMapEvent = useStoreSelector(AppStore, (state) => state.setMapEvent);
    return (data: any) => {
        setMapEvent({
            event: EventSystemNames.onFeatureMouseOver,
            data: data
        });
    }
}

export const useFeatureMouseOutEvent = () => {
    const setMapEvent = useStoreSelector(AppStore, (state) => state.setMapEvent);
    return () => {
        setMapEvent({
            event: EventSystemNames.onFeatureMouseOut,
        });
    }
}

export const useFeatureUnselectEvent = () => {
    const setMapEvent = useStoreSelector(AppStore, (state) => state.setMapEvent);
    return () => {
        setMapEvent({
            event: EventSystemNames.onFeatureUnselect,
        });
    }
}

export const usePilotCertificateCoastalSegmentGroupsEvent = () => {
    const setMapEvent = useStoreSelector(AppStore, (state) => state.setMapEvent);
    return (data:any) => {
        setMapEvent({
            event: EventSystemNames.PilotCertificateCoastalSegmentGroups,
            data: data
        });
        setTimeout(() => {
            setMapEvent(null);
        }, BRIEF_FLAG_TIMEOUT_MS);
    }
}

export const useRefreshedData = () => {
    return useStoreSelector(AppStore, (state) => state.refreshedData);
}
export const useSetRefreshedData = () => {
    const setRefreshedData = useStoreSelector(AppStore, (state) => state.setRefreshedData);
    return () => {
        setRefreshedData(true);
        setTimeout(() => {
            setRefreshedData(false);
        }, BRIEF_FLAG_TIMEOUT_MS);
    }
}

export const useIsMapMini = () => {
    return useStoreSelector(AppStore, (state) => state.isMenuMini);
}
export const useSetIsMapMini = () => {
    return useStoreSelector(AppStore, (state) => state.setIsMenuMini);
}

export const useServiceWorkerRegistration = () => {
    return useStoreSelector(AppStore, (state) => state.serviceWorkerRegistration);
}
export const useSetServiceWorkerRegistration = () => {
    return useStoreSelector(AppStore, (state) => state.setServiceWorkerRegistration);
}

export const AppNotificationSystemNames = {
    MapAisFetchDataError: 'MapAisFetchDataError',
    MapAisFetchShipDataError: 'MapAisFetchShipDataError',
}

export const useSuccessNotification = () => {
    const setNotificationMessage = useStoreSelector(AppStore, (state) => state.setNotificationMessage);
    return (message: string) => {
        setTimeout(() => {
            setNotificationMessage({
                id: uuid(),
                type: "success",
                text: message,
                fadeout: true,
            });
        }, 5);
    }
}
export const useInfoNotification = () => {
    const setNotificationMessage = useStoreSelector(AppStore, (state) => state.setNotificationMessage);
    return (message: string, fadeout = true) => {
        setNotificationMessage({
            id: uuid(),
            type: "info",
            text: message,
            fadeout: fadeout,
        });
    }
}

export const useWarningNotification = () => {
    const setNotificationMessage = useStoreSelector(AppStore, (state) => state.setNotificationMessage);
    return (message: string, systemName = "", fadeout = false) => {
        setNotificationMessage({
            id: uuid(),
            type: "warning",
            text: message,
            fadeout: fadeout,
            systemName: systemName
        });
    }
}

export const useErrorNotification = () => {
    const setNotificationMessage = useStoreSelector(AppStore, (state) => state.setNotificationMessage);
    return (message: string, fadeout = false) => {
        setNotificationMessage({
            id: uuid(),
            type: "error",
            text: message,
            fadeout: fadeout
        });
    }
}
